import React, { useState, useEffect } from 'react';
import styles from '../styles/InventoryList.module.css';
import QuickAction from '../components/QuickAction';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const BRANCHES = [
  { id: 'al_wakra', label: 'Al Wakrah' },
  { id: 'gewan', label: 'Gewan Island' },
];

const CATEGORY_META = {
  Coffee: {
    icon: '☕',
    color: '#f3e5ab',
    subcategories: ['Single Origin Coffee', 'Blends'],
  },
  Milk: {
    icon: '🥛',
    color: '#e3f2fd',
    subcategories: ['Dairy Milk', 'Plant-Based Milk'],
    milkOrder: [
      'Full Fat', 'Low Fat', 'Skimmed', 'Oat', 'Coconut', 'Soy', 'Almond', 'Other'
    ],
  },
  Dairy: { icon: '🧀', color: '#fffde7' },
  Supplies: { icon: '📦', color: '#f1f8e9' },
  Syrups: { icon: '🍯', color: '#fff3e0' },
  Powders: { icon: '🥄', color: '#ede7f6' },
  Equipment: { icon: '⚙️', color: '#eceff1' },
  Disposables: { icon: '🛍️', color: '#fce4ec' },
  Cleaning: { icon: '🧹', color: '#e0f7fa' },
};

const ALL_CATEGORIES = Object.keys(CATEGORY_META);

function getStatus(item) {
  const ratio = item.on_hand / item.par_level;
  if (item.on_hand <= 0) return styles.outOfStock;
  if (ratio < 0.25) return styles.critical;
  if (ratio < 0.5) return styles.low;
  return styles.inStock;
}

export default function InventoryList() {
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [branch, setBranch] = useState('al_wakra');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    const colRef = collection(db, 'inventoryItems');
    const unsubscribe = onSnapshot(colRef, (snap) => {
      let data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (branch) {
        data = data.filter(item => item.branch === branch);
      }
      const normalizedData = data.map(item => {
        let category = item.category || 'Other';
        if (category.toLowerCase() === 'mlk') category = 'Milk';
        if (category.toLowerCase() === 'bars') category = 'Bags';
        return { ...item, category };
      });
      setItems(normalizedData);
      setLoading(false);
    }, (err) => {
      setError('Failed to load inventory.');
      setLoading(false);
    });
    return () => unsubscribe();
  }, [branch]);

  const handleQuickAction = (item) => {
    setSelectedItem(item);
    setShowQuickAction(true);
  };

  const toggleCategoryCollapse = category => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const groupedItems = items.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  const sortedCategories = ALL_CATEGORIES.filter(cat => groupedItems[cat])
    .concat(Object.keys(groupedItems).filter(cat => !ALL_CATEGORIES.includes(cat)))
    .sort((a, b) => {
      const order = ['Coffee', 'Milk'];
      const indexA = order.indexOf(a);
      const indexB = order.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  return (
    <div className={styles.container}>
      <div className={styles.branchSelector}>
        {BRANCHES.map(b => (
          <button
            key={b.id}
            className={branch === b.id ? styles.branchActive : styles.branchBtn}
            onClick={() => setBranch(b.id)}
          >
            {b.label}
          </button>
        ))}
      </div>
      <h1>Inventory</h1>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : items.length === 0 ? (
        <p className={styles.noItems}>No inventory items found for this branch.</p>
      ) : (
        <div className={styles.categoryGridContainer}>
          {sortedCategories.map(category => (
            <div key={category} className={styles.categorySection}>
              <div className={styles.categoryHeader} onClick={() => toggleCategoryCollapse(category)}>
                {category.toUpperCase()} ({groupedItems[category].length})
                <span className={styles.collapseToggle}>
                  {collapsedCategories[category] ? '▼' : '▲'}
                </span>
              </div>
              {!collapsedCategories[category] && (
                <div className={styles.grid}>
                  {groupedItems[category]
                    .sort((a, b) => {
                      if (category === 'Coffee') {
                        return (a.name || '').localeCompare(b.name || '');
                      } else if (category === 'Milk' && CATEGORY_META.Milk.milkOrder) {
                        const milkOrder = CATEGORY_META.Milk.milkOrder;
                        const typeA = (a.subCategory || a.name || '').split(' ')[0];
                        const typeB = (b.subCategory || b.name || '').split(' ')[0];
                        const indexA = milkOrder.indexOf(typeA);
                        const indexB = milkOrder.indexOf(typeB);
                        if (indexA === -1 && indexB === -1) return (a.name || '').localeCompare(b.name || '');
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;
                        return indexA - indexB;
                      } else {
                        return (a.name || '').localeCompare(b.name || '');
                      }
                    })
                    .map(item => (
                      <div
                        key={item.id}
                        className={`${styles.card} ${getStatus(item)}`}
                        onClick={() => handleQuickAction(item)}
                      >
                        <h3>{item.name}</h3>
                        <p className={styles.amount}>{isNaN(item.on_hand) || item.on_hand === null ? '0' : item.on_hand} {item.unit}</p>
                        <p className={styles.category}>{item.category}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <button className={styles.fab} onClick={() => setShowQuickAction(true)}>+</button>
      {showQuickAction && (
        <QuickAction
          item={selectedItem}
          branch={branch}
          onClose={() => {
            setShowQuickAction(false);
            setSelectedItem(null);
          }}
        />
      )}
    </div>
  );
} 